<template>
    <div class="flex mt-[42px] mb-[42px] justify-between h-[42px]">
        <h1
            class="text-[21px] font-medium leading-normal tracking-small text-gray-800"
        >
            {{ $t("vue.domains") }}
        </h1>

        <AtomicButton
            v-if="hasReportsAccess && !demoMode"
            color="yellow"
            size="sm"
            class="group inline-flex items-center justify-center rounded-md border font-medium transition duration-100 ease-in-out hover:shadow-button focus:outline-hidden focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed border-transparent bg-[#088BEA] text-white hover:text-gray-100 focus:text-gray-100 focus:ring-[#088BEA] disabled:bg-gray-300 disabled:text-white h-10 text-sm leading-10 pl-2.5 pr-4"
            :class="{
                'cursor-not-allowed opacity-60': !canManipulateDomains,
            }"
            :aria-disabled="(!canManipulateDomains).toString()"
            :title="domainManipulationDisabledMessage"
            @click="goToDomainWizardBlank"
        >
            <template #icon>
                <PlusCircleIcon class="mr-2 h-4 w-4" />
            </template>
            {{ t("vue.add_new_domain") }}
        </AtomicButton>
    </div>

    <PastDueWarningBlock class="mb-[42px] mt-[42px]" />

    <div class="mb-[42px]">
        <FreePlanReportsLimitBlock />
    </div>

    <div v-if="showDomainsOverview" class="mb-[21px]">
        <DomainsOverviewDetail
            v-if="showDomainsOverview"
            class="mt-[42px]"
            :total-domains="hardTotalDomainsCount"
            :total-active-domains="hardActiveDomainsCount"
            :total-inactive-domains="hardInactiveDomainsCount"
            :empty-domains-slots="emptyDomainsSlots"
            :total-emails-reported="totalEmailsReported"
            :total-parked-domains-count="totalParkedDomainsCount"
            :total-emails-reported-count="totalEmailsReportedCount"
            :total-domains-count="totalDomainsCount"
        />
    </div>

    <div
        v-if="hardTotalDomainsCount === 0 && hasReportsAccess"
        :class="{ 'mt-[42px]': showDomainsOverview }"
    >
        <InfoCard
            :img-src="searchSvg"
            img-alt="Domains none image"
            :title="t('vue.none_domains_yet')"
            :body-text="t('vue.none_domains_yet_instruction')"
            :show-dot="false"
        >
            <template #actions>
                <AtomicButton
                    v-if="!demoMode"
                    color="yellow"
                    size="md"
                    class="h-8 px-4 text-[12px] font-semibold leading-4"
                    :class="{
                        'cursor-not-allowed opacity-60': !canManipulateDomains,
                    }"
                    :aria-disabled="(!canManipulateDomains).toString()"
                    :title="domainManipulationDisabledMessage"
                    @click="goToDomainWizardBlank"
                >
                    {{ t("vue.add_new_domain") }}
                </AtomicButton>
            </template>
        </InfoCard>
    </div>

    <div v-else-if="!hasReportsAccess && hardTotalDomainsCount === 0">
        <InfoCard
            :img-src="trialEndedImg"
            img-alt="Trial ended"
            :title="t('vue.your_trial_ended')"
            :body-text="t('vue.your_trial_ended_action_step')"
            :emphasis-text="t('vue.your_trial_ended_action_step_continue')"
            :button-label="t('vue.choose_dmarceye_plan')"
            :button-href="route('billing.subscriptions.index')"
        />

        <!--        <InfoCard-->
        <!--            :img-src="lockedNotAuthorized"-->
        <!--            img-alt="User has no access to content"-->
        <!--            :title="t('vue.upgrade_required')"-->
        <!--            :body-text="t('vue.not_having_access_message')"-->
        <!--            :show-dot="false"-->
        <!--            :button-label="t('vue.upgrade_my_plan')"-->
        <!--            :button-href="route('billing.subscriptions.index')"-->
        <!--        />-->
    </div>

    <div v-else-if="hardTotalDomainsCount > 0">
        <div class="domains-table-block mt-[42px]">
            <div
                class="domains-table-content-block mb-40 border border-gray-200 bg-white"
            >
                <!-- Card header -->
                <div
                    class="px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-4"
                >
                    <div class="min-w-0">
                        <div class="text-[14px] font-semibold text-gray-900">
                            {{ t("vue.domain_overview_table_header") }}
                        </div>
                        <div class="mt-1 text-[12px] text-gray-500">
                            {{ t("vue.list_of_all_domains_sub_text") }}
                        </div>
                    </div>

                    <!-- Search -->
                    <div class="relative w-[280px] max-w-full shrink-0">
                        <MagnifyingGlassIcon
                            class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                        />
                        <input
                            v-model="searchQuery"
                            type="text"
                            :placeholder="t('vue.search_domains')"
                            class="h-9 w-full rounded-md border border-gray-200 bg-white pl-9 pr-3 text-[13px] text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
                            @keydown.enter.prevent="applyFilters({ page: 1 })"
                        />
                    </div>
                </div>

                <!-- Filters -->
                <div
                    class="px-6 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3"
                >
                    <div class="flex flex-wrap items-center gap-3">
                        <div class="flex items-center gap-2">
                            <FunnelIcon class="h-4 w-4 text-gray-500" />
                            <span class="text-sm font-medium text-gray-700">
                                {{ t("vue.filters") }}:
                            </span>
                        </div>

                        <button
                            type="button"
                            class="inline-flex h-8 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border px-3 text-sm font-medium outline-hidden transition-all focus-visible:ring-[3px]"
                            :class="
                                parkedFilter === 'parked'
                                    ? 'border-gray-800 bg-gray-800 text-white hover:bg-gray-900'
                                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-100'
                            "
                            @click="toggleParkedFilter('parked')"
                        >
                            {{ t("vue.domains_filter_parked") }}
                        </button>

                        <button
                            type="button"
                            class="inline-flex h-8 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border px-3 text-sm font-medium outline-hidden transition-all focus-visible:ring-[3px]"
                            :class="
                                parkedFilter === 'unparked'
                                    ? 'border-gray-800 bg-gray-800 text-white hover:bg-gray-900'
                                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-100'
                            "
                            @click="toggleParkedFilter('unparked')"
                        >
                            {{ t("vue.domains_filter_unparked") }}
                        </button>

                        <button
                            v-if="hasDomainFilters"
                            type="button"
                            class="inline-flex h-8 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md px-3 text-sm font-medium text-gray-600 outline-hidden transition-all hover:bg-gray-100 hover:text-gray-900 focus-visible:ring-[3px]"
                            @click="clearDomainFilters()"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-x h-3 w-3"
                                aria-hidden="true"
                            >
                                <path d="M18 6 6 18"></path>
                                <path d="m6 6 12 12"></path>
                            </svg>
                            {{ t("vue.clear_all") }}
                        </button>

                        <div class="flex items-center gap-2 pl-1">
                            <RectangleGroupIcon class="h-4 w-4 text-gray-500" />
                            <span class="text-sm font-medium text-gray-700">
                                {{ t("vue.subdomain_view_label") }}:
                            </span>
                        </div>

                        <button
                            type="button"
                            class="inline-flex h-8 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border px-3 text-sm font-medium outline-hidden transition-all focus-visible:ring-[3px]"
                            :class="
                                subdomainView === 'name'
                                    ? 'border-gray-800 bg-gray-800 text-white hover:bg-gray-900'
                                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-100'
                            "
                            @click="setSubdomainView('name')"
                        >
                            {{ t("vue.subdomain_view_by_name") }}
                        </button>

                        <button
                            type="button"
                            class="inline-flex h-8 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border px-3 text-sm font-medium outline-hidden transition-all focus-visible:ring-[3px]"
                            :class="
                                subdomainView === 'dmarc'
                                    ? 'border-gray-800 bg-gray-800 text-white hover:bg-gray-900'
                                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-100'
                            "
                            @click="setSubdomainView('dmarc')"
                        >
                            {{ t("vue.subdomain_view_by_dmarc") }}
                        </button>
                    </div>

                    <div class="text-sm text-gray-600">
                        <span v-if="hasDomainFilters">
                            {{
                                t("vue.showing_results_of", {
                                    shown: pagination.total ?? domains.length,
                                    total: hardTotalDomainsCount,
                                })
                            }}
                        </span>
                        <span v-else>
                            {{
                                t("vue.total_results", {
                                    total: hardTotalDomainsCount,
                                })
                            }}
                        </span>
                    </div>
                </div>

                <!-- Table -->
                <div class="overflow-x-auto">
                    <table
                        class="w-full table-fixed [&_thead_th]:px-6 [&_thead_th]:py-0 [&_thead_th]:h-[46px] [&_thead_th]:align-middle [&_thead_th]:text-left [&_thead_th]:text-sm [&_thead_th]:font-medium [&_thead_th]:text-gray-600 [&_tbody_td]:px-6 [&_tbody_td]:py-4 [&_tbody_td]:align-middle"
                    >
                        <colgroup>
                            <col class="w-[42%]" />
                            <col class="w-[14%]" />
                            <col class="w-[16%]" />
                            <col class="w-[calc(28%_-_56px)]" />
                            <col class="w-[56px]" />
                        </colgroup>

                        <thead class="border-b border-gray-100 bg-gray-50">
                            <tr>
                                <th
                                    class="min-w-[260px] px-6 text-left text-sm font-medium text-gray-600"
                                >
                                    <span
                                        class="inline-flex min-w-0 items-center gap-2"
                                    >
                                        <button
                                            type="button"
                                            class="rounded px-1 -mx-1"
                                            :class="
                                                isSortedBy('domain')
                                                    ? 'bg-gray-100'
                                                    : ''
                                            "
                                            @click="setSort('domain', 'asc')"
                                        >
                                            {{
                                                t("vue.dashboard_table_domain")
                                            }}
                                        </button>

                                        <span
                                            class="inline-flex flex-col -space-y-[2px]"
                                        >
                                            <button
                                                type="button"
                                                class="rounded-sm p-[1px] hover:bg-gray-100"
                                                aria-label="Sort ascending"
                                                @click.stop="
                                                    setSort('domain', 'asc')
                                                "
                                            >
                                                <ChevronUpIcon
                                                    class="h-3.5 w-3.5 transition-colors"
                                                    :class="
                                                        isActiveSort(
                                                            'domain',
                                                            'asc',
                                                        )
                                                            ? 'text-gray-700'
                                                            : 'text-gray-300 hover:text-gray-500'
                                                    "
                                                />
                                            </button>

                                            <button
                                                type="button"
                                                class="rounded-sm p-[1px] hover:bg-gray-100"
                                                aria-label="Sort descending"
                                                @click.stop="
                                                    setSort('domain', 'desc')
                                                "
                                            >
                                                <ChevronDownIcon
                                                    class="h-3.5 w-3.5 transition-colors"
                                                    :class="
                                                        isActiveSort(
                                                            'domain',
                                                            'desc',
                                                        )
                                                            ? 'text-gray-700'
                                                            : 'text-gray-300 hover:text-gray-500'
                                                    "
                                                />
                                            </button>
                                        </span>
                                    </span>
                                </th>
                                <!--                                <th-->
                                <!--                                    class="min-w-[140px] px-6 py-4 text-left text-sm font-medium text-gray-600 whitespace-nowrap"-->
                                <!--                                >-->
                                <!--                                    <span-->
                                <!--                                        class="inline-flex items-center gap-2"-->
                                <!--                                    >-->
                                <!--                                        <button-->
                                <!--                                            type="button"-->
                                <!--                                            class="rounded px-1 -mx-1"-->
                                <!--                                            :class="-->
                                <!--                                                isSortedBy('slot_type')-->
                                <!--                                                    ? 'bg-gray-100'-->
                                <!--                                                    : ''-->
                                <!--                                            "-->
                                <!--                                            @click="setSort('slot_type', 'asc')"-->
                                <!--                                        >-->
                                <!--                                            {{ t("vue.slot_type") }}-->
                                <!--                                        </button>-->

                                <!--                                        <span-->
                                <!--                                            class="inline-flex flex-col -space-y-[2px]"-->
                                <!--                                        >-->
                                <!--                                            <button-->
                                <!--                                                type="button"-->
                                <!--                                                class="rounded-sm p-[1px] hover:bg-gray-100"-->
                                <!--                                                aria-label="Sort ascending"-->
                                <!--                                                @click.stop="-->
                                <!--                                                    setSort('slot_type', 'asc')-->
                                <!--                                                "-->
                                <!--                                            >-->
                                <!--                                                <ChevronUpIcon-->
                                <!--                                                    class="h-3.5 w-3.5 transition-colors"-->
                                <!--                                                    :class="-->
                                <!--                                                        isActiveSort(-->
                                <!--                                                            'slot_type',-->
                                <!--                                                            'asc',-->
                                <!--                                                        )-->
                                <!--                                                            ? 'text-gray-700'-->
                                <!--                                                            : 'text-gray-300 hover:text-gray-500'-->
                                <!--                                                    "-->
                                <!--                                                />-->
                                <!--                                            </button>-->

                                <!--                                            <button-->
                                <!--                                                type="button"-->
                                <!--                                                class="rounded-sm p-[1px] hover:bg-gray-100"-->
                                <!--                                                aria-label="Sort descending"-->
                                <!--                                                @click.stop="-->
                                <!--                                                    setSort('slot_type', 'desc')-->
                                <!--                                                "-->
                                <!--                                            >-->
                                <!--                                                <ChevronDownIcon-->
                                <!--                                                    class="h-3.5 w-3.5 transition-colors"-->
                                <!--                                                    :class="-->
                                <!--                                                        isActiveSort(-->
                                <!--                                                            'slot_type',-->
                                <!--                                                            'desc',-->
                                <!--                                                        )-->
                                <!--                                                            ? 'text-gray-700'-->
                                <!--                                                            : 'text-gray-300 hover:text-gray-500'-->
                                <!--                                                    "-->
                                <!--                                                />-->
                                <!--                                            </button>-->
                                <!--                                        </span>-->
                                <!--                                    </span>-->
                                <!--                                </th>-->
                                <th
                                    class="min-w-[140px] px-6 py-4 text-left text-sm font-medium text-gray-600 whitespace-nowrap"
                                >
                                    <span
                                        class="inline-flex items-center gap-2"
                                    >
                                        <button
                                            type="button"
                                            class="rounded px-1 -mx-1"
                                            :class="
                                                isSortedBy('active')
                                                    ? 'bg-gray-100'
                                                    : ''
                                            "
                                            @click="setSort('active', 'asc')"
                                        >
                                            {{ t("vue.status") }}
                                        </button>

                                        <span
                                            class="inline-flex flex-col -space-y-[2px]"
                                        >
                                            <button
                                                type="button"
                                                class="rounded-sm p-[1px] hover:bg-gray-100"
                                                aria-label="Sort ascending"
                                                @click.stop="
                                                    setSort('active', 'asc')
                                                "
                                            >
                                                <ChevronUpIcon
                                                    class="h-3.5 w-3.5 transition-colors"
                                                    :class="
                                                        isActiveSort(
                                                            'active',
                                                            'asc',
                                                        )
                                                            ? 'text-gray-700'
                                                            : 'text-gray-300 hover:text-gray-500'
                                                    "
                                                />
                                            </button>

                                            <button
                                                type="button"
                                                class="rounded-sm p-[1px] hover:bg-gray-100"
                                                aria-label="Sort descending"
                                                @click.stop="
                                                    setSort('active', 'desc')
                                                "
                                            >
                                                <ChevronDownIcon
                                                    class="h-3.5 w-3.5 transition-colors"
                                                    :class="
                                                        isActiveSort(
                                                            'active',
                                                            'desc',
                                                        )
                                                            ? 'text-gray-700'
                                                            : 'text-gray-300 hover:text-gray-500'
                                                    "
                                                />
                                            </button>
                                        </span>
                                    </span>
                                </th>

                                <th
                                    class="min-w-[160px] px-6 py-4 text-left text-sm font-medium text-gray-600 whitespace-nowrap"
                                >
                                    <span
                                        class="inline-flex items-center gap-2"
                                    >
                                        <button
                                            type="button"
                                            class="rounded px-1 -mx-1"
                                            :class="
                                                isSortedBy('created_at')
                                                    ? 'bg-gray-100'
                                                    : ''
                                            "
                                            @click="
                                                setSort('created_at', 'asc')
                                            "
                                        >
                                            {{ t("vue.created_at") }}
                                        </button>

                                        <span
                                            class="inline-flex flex-col -space-y-[2px]"
                                        >
                                            <button
                                                type="button"
                                                class="rounded-sm p-[1px] hover:bg-gray-100"
                                                aria-label="Sort ascending"
                                                @click.stop="
                                                    setSort('created_at', 'asc')
                                                "
                                            >
                                                <ChevronUpIcon
                                                    class="h-3.5 w-3.5 transition-colors"
                                                    :class="
                                                        isActiveSort(
                                                            'created_at',
                                                            'asc',
                                                        )
                                                            ? 'text-gray-700'
                                                            : 'text-gray-300 hover:text-gray-500'
                                                    "
                                                />
                                            </button>

                                            <button
                                                type="button"
                                                class="rounded-sm p-[1px] hover:bg-gray-100"
                                                aria-label="Sort descending"
                                                @click.stop="
                                                    setSort(
                                                        'created_at',
                                                        'desc',
                                                    )
                                                "
                                            >
                                                <ChevronDownIcon
                                                    class="h-3.5 w-3.5 transition-colors"
                                                    :class="
                                                        isActiveSort(
                                                            'created_at',
                                                            'desc',
                                                        )
                                                            ? 'text-gray-700'
                                                            : 'text-gray-300 hover:text-gray-500'
                                                    "
                                                />
                                            </button>
                                        </span>
                                    </span>
                                </th>

                                <th
                                    class="min-w-[180px] px-6 py-4 text-left text-sm font-medium text-gray-600 whitespace-nowrap"
                                >
                                    <span
                                        class="inline-flex items-center gap-2"
                                    >
                                        <button
                                            type="button"
                                            class="rounded px-1 -mx-1"
                                            :class="
                                                isSortedBy('dmarc_policy')
                                                    ? 'bg-gray-100'
                                                    : ''
                                            "
                                            @click="
                                                setSort('dmarc_policy', 'asc')
                                            "
                                        >
                                            {{ t("vue.dmarc_policy") }}
                                        </button>

                                        <span
                                            class="inline-flex flex-col -space-y-[2px]"
                                        >
                                            <button
                                                type="button"
                                                class="rounded-sm p-[1px] hover:bg-gray-100"
                                                aria-label="Sort ascending"
                                                @click.stop="
                                                    setSort(
                                                        'dmarc_policy',
                                                        'asc',
                                                    )
                                                "
                                            >
                                                <ChevronUpIcon
                                                    class="h-3.5 w-3.5 transition-colors"
                                                    :class="
                                                        isActiveSort(
                                                            'dmarc_policy',
                                                            'asc',
                                                        )
                                                            ? 'text-gray-700'
                                                            : 'text-gray-300 hover:text-gray-500'
                                                    "
                                                />
                                            </button>

                                            <button
                                                type="button"
                                                class="rounded-sm p-[1px] hover:bg-gray-100"
                                                aria-label="Sort descending"
                                                @click.stop="
                                                    setSort(
                                                        'dmarc_policy',
                                                        'desc',
                                                    )
                                                "
                                            >
                                                <ChevronDownIcon
                                                    class="h-3.5 w-3.5 transition-colors"
                                                    :class="
                                                        isActiveSort(
                                                            'dmarc_policy',
                                                            'desc',
                                                        )
                                                            ? 'text-gray-700'
                                                            : 'text-gray-300 hover:text-gray-500'
                                                    "
                                                />
                                            </button>
                                        </span>
                                    </span>
                                </th>

                                <th class="px-6 py-4"></th>
                            </tr>
                        </thead>

                        <tbody class="divide-y divide-gray-100">
                            <template
                                v-for="node in domains"
                                :key="nodeKey(node)"
                            >
                                <template v-if="isOtherGroup(node)">
                                    <Domain
                                        :domain="otherParentDomain(node)"
                                        :has-children="
                                            (node.children ?? []).length > 0
                                        "
                                        :is-expanded="otherExpanded"
                                        :subdomains-count="
                                            (node.children ?? []).length
                                        "
                                        :hide-actions="true"
                                        :disable-click="true"
                                        :creating-from-reports="
                                            creatingDomainFromReports ===
                                            otherParentDomain(node).domain
                                        "
                                        @toggle="toggleOther"
                                    />

                                    <template v-if="otherExpanded">
                                        <Domain
                                            v-for="child in node.children ?? []"
                                            :key="child.domain"
                                            :domain="child"
                                            :is-subdomain-row="true"
                                            :hide-actions="
                                                child.isExternal === true
                                            "
                                            :disable-click="
                                                child.isExternal === true
                                            "
                                            :creating-from-reports="
                                                creatingDomainFromReports ===
                                                child.domain
                                            "
                                            @remove="removeDomain"
                                            @refreshDomains="refreshDomains"
                                            @validate="validateDomain"
                                            @toggle-park="
                                                handleParkDomainToggle
                                            "
                                            @create-domain-parked-from-reports="
                                                createDomainParkedFromReports
                                            "
                                        />
                                    </template>
                                </template>

                                <template v-else>
                                    <Domain
                                        :domain="node"
                                        :has-children="
                                            (node.children ?? []).length > 0
                                        "
                                        :is-expanded="isExpanded(node.domain)"
                                        :subdomains-count="
                                            node.subdomainsCount ??
                                            (node.children ?? []).length
                                        "
                                        :creating-from-reports="
                                            creatingDomainFromReports ===
                                            node.domain
                                        "
                                        @toggle="toggleDomain(node.domain)"
                                        @remove="removeDomain"
                                        @refreshDomains="refreshDomains"
                                        @validate="validateDomain"
                                        @toggle-park="handleParkDomainToggle"
                                        @create-domain-parked-from-reports="
                                            createDomainParkedFromReports
                                        "
                                    />

                                    <template v-if="isExpanded(node.domain)">
                                        <Domain
                                            v-for="child in node.children ?? []"
                                            :key="child.domain"
                                            :domain="child"
                                            :is-subdomain-row="true"
                                            :hide-actions="
                                                child.isExternal === true
                                            "
                                            :disable-click="
                                                child.isExternal === true
                                            "
                                            :creating-from-reports="
                                                creatingDomainFromReports ===
                                                child.domain
                                            "
                                            @remove="removeDomain"
                                            @refreshDomains="refreshDomains"
                                            @validate="validateDomain"
                                            @toggle-park="
                                                handleParkDomainToggle
                                            "
                                            @create-domain-parked-from-reports="
                                                createDomainParkedFromReports
                                            "
                                        />
                                    </template>
                                </template>
                            </template>
                        </tbody>
                    </table>
                </div>

                <!-- Footer -->
                <div
                    class="px-6 py-4 border-t border-gray-100 flex items-center gap-4"
                >
                    <!-- Left text -->
                    <p class="text-[12px] text-gray-500 min-w-0 truncate">
                        {{ t("vue.showing") }}
                        {{ pagination.from ?? (domains.length ? 1 : 0) }}
                        {{ t("vue.showing_to") }}
                        {{ pagination.to ?? domains.length }}
                        {{ t("vue.showing_of") }}
                        {{ pagination.total ?? domains.length }}
                        {{ t("vue.showing_results") }}
                    </p>

                    <div
                        class="ml-auto flex items-center gap-4 min-w-0 overflow-hidden"
                    >
                        <!-- Per page -->
                        <div class="shrink-0">
                            <div
                                class="inline-flex h-9 overflow-hidden rounded-md border border-gray-200 bg-white"
                            >
                                <div class="relative h-9">
                                    <!-- klikatelné celé -->
                                    <select
                                        v-model.number="perPageValue"
                                        class="absolute inset-0 z-10 h-full w-full cursor-pointer appearance-none bg-transparent px-2 pr-8 text-[13px] text-transparent focus:outline-none"
                                        aria-label="Items per page"
                                        @change="commitPerPage"
                                    >
                                        <option
                                            v-for="n in PER_PAGE_OPTIONS"
                                            :key="n"
                                            :value="n"
                                        >
                                            {{ n }}
                                        </option>
                                    </select>

                                    <!-- vizuální obsah -->
                                    <div
                                        class="pointer-events-none flex h-9 items-center gap-2 px-2 pr-8"
                                    >
                                        <span
                                            class="text-[13px] text-gray-700"
                                            >{{ perPageValue }}</span
                                        >
                                        <span
                                            class="text-[13px] text-gray-700"
                                            >{{ t("vue.per_page") }}</span
                                        >
                                    </div>

                                    <!-- šipka až za textem (vpravo) -->
                                    <ChevronDownIcon
                                        class="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- Pagination: scrolls inside itself, never expands table/card -->
                        <nav
                            class="min-w-0 flex-1 inline-flex items-center gap-1 overflow-x-auto whitespace-nowrap"
                            aria-label="Pagination"
                        >
                            <button
                                class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-transparent"
                                :disabled="pagination.current_page <= 1"
                                @click="goToPage(pagination.current_page - 1)"
                            >
                                <ChevronLeftIcon class="h-4 w-4" />
                            </button>

                            <button
                                v-for="page in visiblePages"
                                :key="page"
                                :disabled="page === '...'"
                                class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-[13px]"
                                :class="
                                    page === pagination.current_page
                                        ? 'bg-gray-100 font-medium text-gray-900'
                                        : page === '...'
                                          ? 'text-gray-400'
                                          : 'text-gray-600 hover:bg-gray-50'
                                "
                                @click="
                                    page !== '...' && goToPage(page as number)
                                "
                            >
                                {{ page }}
                            </button>

                            <button
                                class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-transparent"
                                :disabled="
                                    pagination.current_page >=
                                    pagination.last_page
                                "
                                @click="goToPage(pagination.current_page + 1)"
                            >
                                <ChevronRightIcon class="h-4 w-4" />
                            </button>
                        </nav>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <CreateDomainModal
        v-if="createDomainModal"
        @close="closeCreateDomainModal"
    />

    <DomainLimitModal
        :show="showDomainLimitModal"
        :message="domainLimitMessage"
        @close="closeDomainLimitModal"
    />
    <DomainUnparkConfirmPopup
        v-model="showUnparkConfirmPopup"
        mode="domain-unpark"
        :empty-domains-slots="emptyDomainsSlots"
        :unparked-domains-count="unparkedDomainsCount"
        @confirm="confirmUnparkDomain"
    />

    <TrialEndedPopup />
    <!--    <ExtendYourTrialPopup />-->
    <!--    <FreePlanPopup />-->
    <FreePlanMaxReachedPopup />
</template>

<script lang="ts" setup>
import { computed, onMounted, type PropType, ref, watch } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import route from "ziggy-js";
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    FunnelIcon,
    MagnifyingGlassIcon,
    PlusCircleIcon,
    RectangleGroupIcon,
} from "@heroicons/vue/24/outline";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/vue/20/solid";
import type IDomain from "@/types/Domains/IDomain";
import { useTranslations } from "@/composables/useTranslations";
import DomainLimitModal from "@/Pages/Domains/components/DomainLimitModal.vue";
import Domain from "@/Pages/Domains/components/Domain.vue";
import CreateDomainModal from "@/Pages/Domains/components/CreateDomainModal.vue";
import axios from "axios";
import { create } from "@/composables/useNotifications";
import useConfirm from "@/composables/useConfirm";
import DomainsOverviewDetail from "@/Pages/Domains/components/DomainsOverviewDetail.vue";
import useReportsAccess from "@/composables/useReportsAccess.ts";
import InfoCard from "@/components/Content/InfoCard.vue";
import { useDomainParking } from "@/composables/useDomainParking";
import FreePlanReportsLimitBlock from "@/Pages/Reports/components/FreePlanReportsLimitBlock.vue";
import PastDueWarningBlock from "@/components/Atomic/Miscellaneous/PastDueWarningBlock.vue";
import FreePlanMaxReachedPopup from "@/components/Atomic/Dialogs/Popups/FreePlanMaxReachedPopup.vue";
import DomainUnparkConfirmPopup from "../../components/Atomic/Dialogs/Popups/DomainUnparkConfirmPopup.vue";
import useDomainManipulationPermissions from "@/composables/UseDomainManipulationPermissions";

type DomainGroup = {
    isOtherGroup: true;
    children: IDomain[];
};

type DomainTreeNode = IDomain | DomainGroup;

const searchSvg = new URL(
    "/resources/js/assets/images/search.svg",
    import.meta.url,
).href;

const trialEndedImg = new URL(
    "/resources/js/assets/images/plan-calendar.svg",
    import.meta.url,
).href;

const { show } = useConfirm();
const { t } = useTranslations();
const { hasReportsAccess } = useReportsAccess();
const {
    canManipulateDomains,
    domainManipulationDisabledMessage,
    ensureCanManipulateDomains,
} = useDomainManipulationPermissions();
const {
    parkDomainToggleWithBasicLimitGuard,
    showDomainLimitModal,
    domainLimitMessage,
    closeDomainLimitModal,
} = useDomainParking();

const demoMode = computed(() => usePage<IRhizomPageProps>().props.demoMode);

const props = defineProps({
    domains: {
        type: Array as PropType<DomainTreeNode[]>,
        default: () => [],
    },
    activeDomains: {
        type: Array as PropType<IDomain[]>,
        default: () => [],
    },
    inactiveDomains: {
        type: [Array, Object] as PropType<IDomain[] | Record<string, IDomain>>,
        default: () => [],
    },
    totalInactiveDomains: {
        type: Number,
        default: 0,
    },
    emptyDomainsSlots: {
        type: Number,
        default: 0,
    },
    totalEmailsReported: {
        type: Number,
        default: 0,
    },
    totalDomainsCount: {
        type: Number,
        default: 0,
    },
    totalActiveDomains: {
        type: Number,
        default: 0,
    },
    hardTotalDomainsCount: {
        type: Number,
        default: 0,
    },
    hardActiveDomainsCount: {
        type: Number,
        default: 0,
    },
    hardInactiveDomainsCount: {
        type: Number,
        default: 0,
    },
    totalParkedDomainsCount: {
        type: Number,
        default: 0,
    },
    totalEmailsReportedCount: {
        type: Number,
        default: 0,
    },
    showDomainsOverview: {
        type: Boolean,
        default: false,
    },
    subdomainViewMode: {
        type: String,
        default: "name",
    },
    pagination: {
        type: Object as PropType<{
            current_page: number;
            last_page: number;
            per_page: number;
            total: number;
            from: number | null;
            to: number | null;
        }>,
        default: () => ({
            current_page: 1,
            last_page: 1,
            per_page: 20,
            total: 0,
            from: 0,
            to: 0,
        }),
    },
});

const paramsFromUrl = () => new URLSearchParams(window.location.search);

const PER_PAGE_OPTIONS = [10, 25, 50, 100] as const;
const PER_PAGE_DEFAULT = 25;

const createDomainModal = ref(false);
const validationLoading = ref(false);
const creatingDomainFromReports = ref<string | null>(null);
const showUnparkConfirmPopup = ref(false);
const pendingUnparkDomain = ref<IDomain | null>(null);

const currentPage = ref(1);
const totalPages = ref(1);
const chunkSize = ref(10);
const totalDomains = ref(0);
const processedDomains = ref(0);

const inactiveDomainsArray = computed(() => {
    if (Array.isArray(props.inactiveDomains)) {
        return props.inactiveDomains;
    }
    return Object.values(props.inactiveDomains || {});
});

const inactiveDomainsEnhanced = ref<IDomain[]>([]);

const isOtherGroup = (node: DomainTreeNode): node is DomainGroup =>
    (node as DomainGroup).isOtherGroup === true;

const nodeKey = (node: DomainTreeNode) =>
    isOtherGroup(node) ? "other-group" : node.domain;

const expandedDomains = ref<Set<string>>(new Set());
const otherExpanded = ref(true);

const otherParentDomain = (node: DomainGroup): IDomain => ({
    account: "",
    domain: t("vue.other"),
    active: false,
    created_at: "",
    updated_at: "",
    dmarcRecord: null,
    dmarcPolicy: null,
    slotType: t("vue.billable"),
    isSubdomain: false,
    subdomainsCount: (node.children ?? []).length,
    isOtherGroup: true,
});

const setDefaultExpanded = (nodes: DomainTreeNode[]) => {
    const next = new Set<string>();

    nodes.forEach((node) => {
        if (isOtherGroup(node)) return;
        if ((node.children ?? []).length > 0) {
            next.add(node.domain);
        }
    });

    expandedDomains.value = next;
};

const isExpanded = (domain: string) => expandedDomains.value.has(domain);

const toggleDomain = (domain: string) => {
    const next = new Set(expandedDomains.value);
    if (next.has(domain)) {
        next.delete(domain);
    } else {
        next.add(domain);
    }

    expandedDomains.value = next;
};

const toggleOther = () => {
    otherExpanded.value = !otherExpanded.value;
};

const closeCreateDomainModal = () => {
    createDomainModal.value = false;

    router.visit(route("domains.index"), {
        method: "get",
        preserveState: false,
        preserveScroll: false,
    });
};

const goToDomainWizardBlank = () => {
    if (!ensureCanManipulateDomains()) {
        return;
    }

    router.get(route("domainGuide.index"));
};

const removeDomain = async (domain: string) => {
    if (!ensureCanManipulateDomains()) {
        return;
    }

    const confirmed = await show({
        header: t("vue.delete_domain"),
        headerColor: "black",
        message: t("vue.are_you_sure_delete_domain"),
        type: "danger",
        confirm: t("vue.delete"),
    });

    if (confirmed) {
        router.delete(route("domains.destroy", { domain: domain }), {
            preserveScroll: true,
            preserveState: true,
            onSuccess: () => {
                create({
                    title: t("vue.domain_removed"),
                    text: t("vue.domain_removed_successfully"),
                    type: "success",
                });
            },
            onError: (e) => {
                create({
                    title: t("vue.error"),
                    text: e.message || t("vue.error_removing_domain"),
                    type: "error",
                });
            },
        });
    }
};

const refreshDomains = () => {
    router.reload({ only: ["domains", "activeDomains", "inactiveDomains"] });
};

const unparkedDomainsCount = computed(() =>
    Math.max(0, props.hardTotalDomainsCount - props.totalParkedDomainsCount),
);

const handleParkDomainToggle = async (domain: IDomain) => {
    if (!ensureCanManipulateDomains()) {
        return;
    }

    if (domain.parked === true) {
        pendingUnparkDomain.value = domain;
        showUnparkConfirmPopup.value = true;
        return;
    }

    const confirmed = await show({
        header: t("vue.park_domain"),
        headerColor: "black",
        message: t("vue.are_you_sure_park_domain"),
        type: "danger",
        confirm: t("vue.park_domain"),
        confirmButtonBackgroundColor: "#F0AD4E",
    });

    if (confirmed !== true) {
        return;
    }

    parkDomainToggleWithBasicLimitGuard(domain, {
        unparkedDomainsCount: unparkedDomainsCount.value,
        onSuccess: () => {
            refreshDomains();
        },
    });
};

const confirmUnparkDomain = () => {
    if (!ensureCanManipulateDomains()) {
        return;
    }

    const domainToUnpark = pendingUnparkDomain.value;

    if (domainToUnpark == null) {
        return;
    }

    parkDomainToggleWithBasicLimitGuard(domainToUnpark, {
        unparkedDomainsCount: unparkedDomainsCount.value,
        onSuccess: () => {
            refreshDomains();
        },
    });
};

const createDomainParkedFromReports = (domain: IDomain) => {
    if (creatingDomainFromReports.value !== null) {
        return;
    }

    creatingDomainFromReports.value = domain.domain;

    router.post(
        route("domains.store"),
        { domain: domain.domain },
        {
            preserveScroll: true,
            preserveState: false,
            onSuccess: () => {
                create({
                    title: t("vue.domain_added"),
                    type: "success",
                });
            },
            onError: (e) => {
                create({
                    title: t("vue.domain_not_added"),
                    text: e.domain ?? e.message ?? t("vue.error_adding_domain"),
                    type: "error",
                });
            },
            onFinish: () => {
                creatingDomainFromReports.value = null;
            },
        },
    );
};

const validateDomain = async (domain: string) => {
    validationLoading.value = true;

    try {
        const result = await axios.post(
            route("domains.validate.store", { domain: domain }),
        );

        if (result.status === 204) {
            create({
                title: t("vue.domain_validated"),
                text: t("vue.domain_validated_successfully"),
                type: "success",
            });

            router.reload({ only: ["activeDomains", "inactiveDomains"] });
        } else {
            create({
                title: t("vue.domain_not_validated"),
                text: t("vue.could_not_validate_domain"),
                type: "warning",
            });
        }
    } catch (e) {
        () => undefined;
    } finally {
        validationLoading.value = false;
    }
};

onMounted(() => {
    inactiveDomainsEnhanced.value = [...inactiveDomainsArray.value];
    totalDomains.value = inactiveDomainsArray.value.length;
});

watch(
    () => props.domains,
    (nodes) => {
        setDefaultExpanded(nodes as DomainTreeNode[]);
    },
    { immediate: true },
);

const pagination = computed(() => props.pagination);

type SortDir = "asc" | "desc";
type ParkedFilter = "all" | "parked" | "unparked";
type SubdomainView = "name" | "dmarc";

const SORTABLE_COLS = new Set([
    "domain",
    "slot_type",
    "active",
    "created_at",
    "dmarc_policy",
]);

const getInitialSort = () => {
    const byRaw = paramsFromUrl().get("sort_by");
    const dirRaw = paramsFromUrl().get("sort_by_direction");

    const by = byRaw && SORTABLE_COLS.has(byRaw) ? byRaw : null;

    let dir: SortDir | null = null;
    if (by) dir = dirRaw?.toLowerCase() === "asc" ? "asc" : "desc";

    return { by, dir };
};

const initialSort = getInitialSort();

const sortBy = ref<string | null>(initialSort.by);
const sortDir = ref<SortDir | null>(initialSort.dir);

const isSortedBy = (col: string) => sortBy.value === col;
const isActiveSort = (col: string, dir: SortDir) =>
    sortBy.value === col && sortDir.value === dir;

const clearSort = () => {
    sortBy.value = null;
    sortDir.value = null;
};

const setSort = (col: string, dir: SortDir) => {
    if (isActiveSort(col, dir)) {
        clearSort();
        applyFilters({ page: 1 });
        return;
    }

    sortBy.value = col;
    sortDir.value = dir;
    applyFilters({ page: 1 });
};

const normalizePerPage = (v: unknown) => {
    const raw = typeof v === "number" ? v : parseInt(String(v ?? ""), 10);
    const n = Number.isFinite(raw) ? Math.floor(raw) : PER_PAGE_DEFAULT;

    let best = PER_PAGE_OPTIONS[0];
    let bestDiff = Math.abs(n - best);

    for (const opt of PER_PAGE_OPTIONS) {
        const d = Math.abs(n - opt);
        if (d < bestDiff) {
            best = opt;
            bestDiff = d;
        }
    }

    return best;
};

const getInitialPerPage = () => {
    const raw = paramsFromUrl().get("per_page");
    if (raw) return normalizePerPage(raw);

    const propVal = Number(props.pagination?.per_page);
    if (Number.isFinite(propVal) && propVal > 0)
        return normalizePerPage(propVal);

    return PER_PAGE_DEFAULT;
};

const getInitialSearch = () => paramsFromUrl().get("search") ?? "";
const normalizeParkedFilter = (v: unknown): ParkedFilter =>
    v === "all" || v === "parked" || v === "unparked" ? v : "all";

const getInitialParkedFilter = (): ParkedFilter =>
    normalizeParkedFilter(paramsFromUrl().get("parked"));

const normalizeSubdomainView = (v: unknown): SubdomainView =>
    v === "dmarc" ? "dmarc" : "name";

const getInitialSubdomainView = (): SubdomainView => {
    const fromUrl = paramsFromUrl().get("subdomain_view");
    if (fromUrl) return normalizeSubdomainView(fromUrl);

    return normalizeSubdomainView(props.subdomainViewMode);
};

const searchQuery = ref<string>(getInitialSearch());
const parkedFilter = ref<ParkedFilter>(getInitialParkedFilter());
const subdomainView = ref<SubdomainView>(getInitialSubdomainView());

const setSubdomainView = (mode: SubdomainView) => {
    if (subdomainView.value === mode) return;
    subdomainView.value = mode;
    applyFilters({ page: 1 });
};

const perPageValue = ref<number>(getInitialPerPage());
const perPageDraft = ref<string>(String(perPageValue.value));

const hasDomainFilters = computed(() => parkedFilter.value !== "all");

const toggleParkedFilter = (filter: Exclude<ParkedFilter, "all">) => {
    parkedFilter.value = parkedFilter.value === filter ? "all" : filter;
    applyFilters({ page: 1 });
};

const clearDomainFilters = () => {
    parkedFilter.value = "all";
    applyFilters({ page: 1 });
};

const applyFilters = (opts?: { page?: number }) => {
    const params = paramsFromUrl();

    params.set("page", String(opts?.page ?? 1));

    const q = searchQuery.value.trim();
    if (q) params.set("search", q);
    else params.delete("search");

    params.set("per_page", String(normalizePerPage(perPageValue.value)));

    if (parkedFilter.value !== "all") {
        params.set("parked", parkedFilter.value);
    } else {
        params.delete("parked");
    }

    if (subdomainView.value !== "name") {
        params.set("subdomain_view", subdomainView.value);
    } else {
        params.delete("subdomain_view");
    }

    if (sortBy.value && sortDir.value) {
        params.set("sort_by", sortBy.value);
        params.set("sort_by_direction", sortDir.value);
    } else {
        params.delete("sort_by");
        params.delete("sort_by_direction");
    }

    router.get(route("domains.index"), Object.fromEntries(params), {
        preserveState: true,
        preserveScroll: true,
        replace: true,
        only: ["domains", "pagination"],
    });
};

const commitPerPage = () => {
    const normalized = normalizePerPage(perPageValue.value);
    if (normalized !== perPageValue.value) perPageValue.value = normalized;
    applyFilters({ page: 1 });
};

watch(
    () => props.pagination?.per_page,
    (v) => {
        if (v == null) return;
        perPageValue.value = normalizePerPage(v);
    },
    { immediate: true },
);

watch(showUnparkConfirmPopup, (isOpen) => {
    if (isOpen === false) {
        pendingUnparkDomain.value = null;
    }
});

const visiblePages = computed<(number | string)[]>(() => {
    const cur = pagination.value.current_page;
    const last = pagination.value.last_page;

    const windowSize = 1;
    const pages: (number | string)[] = [];

    pages.push(1);

    if (cur - windowSize > 2) pages.push("...");

    const start = Math.max(2, cur - windowSize);
    const end = Math.min(last - 1, cur + windowSize);
    for (let p = start; p <= end; p++) pages.push(p);

    if (cur + windowSize < last - 1) pages.push("...");

    if (last > 1) pages.push(last);

    return pages.filter((v, i, a) => i === 0 || v !== a[i - 1]);
});

const goToPage = (page: number) => {
    if (!page || page < 1 || page > pagination.value.last_page) return;
    applyFilters({ page });
};

const SEARCH_DEBOUNCE_MS = 300;
let searchTimer: number | undefined;

watch(
    searchQuery,
    () => {
        if (searchTimer) window.clearTimeout(searchTimer);
        searchTimer = window.setTimeout(() => {
            applyFilters({ page: 1 });
        }, SEARCH_DEBOUNCE_MS);
    },
    { flush: "post" },
);
</script>

<style scoped>
/*DOMAINS CONTENT BLOCK*/
.domains-table-block {
    max-width: 1330px;
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 30px;
}

.domains-table-content-block {
    padding: 0;
    border-radius: 12.8px;
    border: 1px solid #e5e7eb;
    background: #fff;
    overflow: hidden;
}

table,
thead,
tbody,
tr,
td,
th {
    overflow: visible;
}
</style>
